import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import AdminLayout from '../../../layouts/AdminLayout';
import unifiedApi from '../../../utils/unifiedApi';
import { usePermissions } from '../../../contexts/PermissionsContext';
import { useOutstandingBalance } from '../../../contexts/OutstandingBalanceContext';
import { InvoiceDetails } from '../../../components/player/OutstandingBalanceSection';
import { formatDateTime, formatDueDate, formatMoney } from '../../../utils/outstandingBalance';

const SORT_OPTIONS = [
  { value: 'amount', label: 'Amount (highest first)' },
  { value: 'name', label: 'Name (A–Z)' },
];

const SPARE_STATUS_OPTIONS = [
  { value: '', label: 'Any role' },
  { value: 'regular', label: 'Regular' },
  { value: 'spare', label: 'Spare' },
];

const displayName = (player) => {
  const name = `${player.firstName || ''} ${player.lastName || ''}`.trim();
  return name || player.customerName || player.email || 'Unknown customer';
};

const sortKey = (player) => `${player.lastName || ''} ${player.firstName || ''}`.trim().toLowerCase()
  || (player.customerName || player.email || '').toLowerCase();

const PlayerName = ({ player }) => (
  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
    {player.hasAccount && player.userTenantId ? (
      <Typography component={RouterLink} to={`/player/${player.userTenantId}`} variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
        {displayName(player)}
      </Typography>
    ) : (
      <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
        {displayName(player)}
      </Typography>
    )}
    {!player.hasAccount && (
      <Chip label="No FootyManager account" size="small" variant="outlined" clickable={false} onClick={() => {}} />
    )}
  </Stack>
);

const Roles = ({ memberships }) => {
  if (!memberships.length) return <Typography variant="body2" color="text.secondary">—</Typography>;
  return (
    <Stack spacing={0.5}>
      {memberships.map((m, index) => (
        <Typography key={`${m.leagueId}-${m.teamId || 'spare'}-${index}`} variant="body2" sx={{ wordBreak: 'break-word' }}>
          <RouterLink to={`/leagues/${m.leagueSlug}`}>{m.leagueName}</RouterLink>
          {' · '}
          {m.teamSlug ? <RouterLink to={`/teams/${m.teamSlug}`}>{m.teamName}</RouterLink> : null}
          {m.teamSlug ? ' · ' : null}
          {m.role}
        </Typography>
      ))}
    </Stack>
  );
};

const InvoiceList = ({ invoices }) => (
  <Stack spacing={1.5} sx={{ py: 1 }}>
    {invoices.map((invoice) => (
      <InvoiceDetails key={invoice.invoiceId} invoice={invoice} actionLabel="Open in Square" />
    ))}
  </Stack>
);

const OutstandingBalancesPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isLoading: permissionsLoading, canViewOutstandingBalances } = usePermissions();
  const canView = canViewOutstandingBalances();
  const { refresh: refreshMyBalance } = useOutstandingBalance();

  const [leagues, setLeagues] = useState([]);
  const [teams, setTeams] = useState([]);
  const [leagueId, setLeagueId] = useState('');
  const [teamId, setTeamId] = useState('');
  const [spareStatus, setSpareStatus] = useState('');
  const [sortBy, setSortBy] = useState('amount');

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncFeedback, setSyncFeedback] = useState(null);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (!canView) return;
    unifiedApi.getLeagues()
      .then((data) => setLeagues(data || []))
      .catch(() => setLeagues([]));
  }, [canView]);

  useEffect(() => {
    setTeams([]);
    if (!leagueId) return undefined;
    let cancelled = false;
    unifiedApi.getTeams(leagueId)
      .then((data) => { if (!cancelled) setTeams(data || []); })
      .catch(() => { if (!cancelled) setTeams([]); });
    return () => { cancelled = true; };
  }, [leagueId]);

  const latestRequestRef = useRef(0);
  const loadReport = useCallback(async () => {
    const requestId = ++latestRequestRef.current;
    const isCurrent = () => requestId === latestRequestRef.current;
    setLoading(true);
    setError(null);
    try {
      const data = await unifiedApi.getOutstandingBalances({ leagueId, teamId, spareStatus });
      if (isCurrent()) setReport(data);
    } catch (err) {
      if (isCurrent()) setError(err.message || 'Failed to load outstanding balances.');
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [leagueId, teamId, spareStatus]);

  // The post-sync reload must use the filters current when the sync finishes, not when it started.
  const loadReportRef = useRef(loadReport);
  useEffect(() => {
    loadReportRef.current = loadReport;
  }, [loadReport]);

  useEffect(() => {
    if (canView) loadReport();
  }, [canView, loadReport]);

  const handleLeagueChange = (e) => {
    setLeagueId(e.target.value);
    setTeamId('');
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncFeedback(null);
    try {
      const result = await unifiedApi.syncOutstandingBalances();
      setSyncFeedback({ severity: 'success', message: `Synced ${result?.invoiceCount ?? 0} open invoices from Square.` });
      await loadReportRef.current();
      refreshMyBalance();
    } catch (err) {
      setSyncFeedback({ severity: 'error', message: err.message || 'Failed to sync from Square.' });
    } finally {
      setSyncing(false);
    }
  };

  const players = useMemo(() => {
    const list = [...(report?.players || [])];
    if (sortBy === 'name') {
      list.sort((a, b) => sortKey(a).localeCompare(sortKey(b)));
    } else {
      list.sort((a, b) => b.totalOverdueCents - a.totalOverdueCents);
    }
    return list;
  }, [report, sortBy]);

  // Show roles for the selected league, otherwise current-season roles.
  const visibleMemberships = (player) => (leagueId
    ? player.memberships.filter((m) => m.leagueId === Number(leagueId))
    : player.memberships.filter((m) => m.isActiveLeague));

  const keyFor = (player) => player.userTenantId || player.email || player.invoices[0]?.invoiceId;
  const toggle = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  if (!canView) {
    return (
      <AdminLayout>
        {permissionsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Alert severity="error">You do not have permission to view outstanding balances.</Alert>
        )}
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
            <WalletIcon sx={{ mr: 1 }} color="primary" />
            Outstanding Balances
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Overdue Square invoices. Last synced: {report?.lastSyncedAt ? formatDateTime(report.lastSyncedAt) : 'never'}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={syncing ? <CircularProgress size={18} /> : <RefreshIcon />}
          onClick={handleSync}
          disabled={syncing}
          fullWidth={isMobile}
        >
          Fetch latest from Square
        </Button>
      </Stack>

      {syncFeedback && (
        <Alert severity={syncFeedback.severity} sx={{ mb: 2 }} onClose={() => setSyncFeedback(null)}>
          {syncFeedback.message}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            select
            label="League"
            value={leagueId}
            onChange={handleLeagueChange}
            size="small"
            sx={{ minWidth: { xs: '100%', md: 220 } }}
          >
            <MenuItem value="">All leagues</MenuItem>
            {leagues.map((league) => (
              <MenuItem key={league.id} value={league.id}>{league.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Team"
            value={teamId}
            onChange={(e) => setTeamId(e.target.value)}
            size="small"
            disabled={!leagueId}
            sx={{ minWidth: { xs: '100%', md: 200 } }}
          >
            <MenuItem value="">All teams</MenuItem>
            {teams.map((team) => (
              <MenuItem key={team.id} value={team.id}>{team.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Role"
            value={spareStatus}
            onChange={(e) => setSpareStatus(e.target.value)}
            size="small"
            sx={{ minWidth: { xs: '100%', md: 160 } }}
          >
            {SPARE_STATUS_OPTIONS.map((o) => (
              <MenuItem key={o.value || 'any'} value={o.value}>{o.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Sort by"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            size="small"
            sx={{ minWidth: { xs: '100%', md: 200 } }}
          >
            {SORT_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </TextField>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : report && (
        <>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            {players.length} {players.length === 1 ? 'person owes' : 'people owe'} a total of <strong>{formatMoney(report.totalOverdueCents)}</strong>
          </Typography>

          {players.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">No overdue balances.</Typography>
            </Paper>
          ) : isMobile ? (
            <Stack spacing={2}>
              {players.map((player) => {
                const key = keyFor(player);
                return (
                  <Paper key={key} sx={{ p: 2 }} data-testid="outstanding-balance-row">
                    <Stack spacing={1}>
                      <PlayerName player={player} />
                      {player.email && (
                        <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>{player.email}</Typography>
                      )}
                      <Roles memberships={visibleMemberships(player)} />
                      <Typography variant="h6" color="error.main">{formatMoney(player.totalOverdueCents)}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {player.invoiceCount} invoice{player.invoiceCount === 1 ? '' : 's'} · oldest due {formatDueDate(player.oldestDueDate)}
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => toggle(key)}
                        endIcon={expanded[key] ? <CollapseIcon /> : <ExpandIcon />}
                        sx={{ alignSelf: 'flex-start' }}
                      >
                        {expanded[key] ? 'Hide invoices' : 'Show invoices'}
                      </Button>
                      <Collapse in={!!expanded[key]} unmountOnExit>
                        <InvoiceList invoices={player.invoices} />
                      </Collapse>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          ) : (
            <Paper sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>Player</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell align="right">Overdue</TableCell>
                    <TableCell align="right">Invoices</TableCell>
                    <TableCell>Oldest due</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {players.map((player) => {
                    const key = keyFor(player);
                    return (
                      <React.Fragment key={key}>
                        <TableRow hover data-testid="outstanding-balance-row">
                          <TableCell padding="checkbox">
                            <IconButton
                              size="small"
                              aria-label={expanded[key] ? 'Hide invoices' : 'Show invoices'}
                              onClick={() => toggle(key)}
                            >
                              {expanded[key] ? <CollapseIcon /> : <ExpandIcon />}
                            </IconButton>
                          </TableCell>
                          <TableCell><PlayerName player={player} /></TableCell>
                          <TableCell sx={{ wordBreak: 'break-all' }}>{player.email || '—'}</TableCell>
                          <TableCell><Roles memberships={visibleMemberships(player)} /></TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>{formatMoney(player.totalOverdueCents)}</TableCell>
                          <TableCell align="right">{player.invoiceCount}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDueDate(player.oldestDueDate)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={7} sx={{ py: 0, borderBottom: expanded[key] ? undefined : 'none' }}>
                            <Collapse in={!!expanded[key]} unmountOnExit>
                              <InvoiceList invoices={player.invoices} />
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          )}
        </>
      )}
    </AdminLayout>
  );
};

export default OutstandingBalancesPage;
